import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Collapse,
	Flex,
	Group,
	Modal,
	NumberInput,
	ScrollArea,
	Select,
	Switch,
	Text,
	useMantineTheme,
} from '@mantine/core';
import { ScenarioEvent, ScenarioEventInputData } from '../../../../types/assessments';
import { Event } from './Event';
import { EventParameters } from './EventParameters';

interface Props {
	value: ScenarioEvent[] | null;
	scenarioEvents: Record<number, ScenarioEventInputData>;
	onChange: (value: ScenarioEvent[] | null) => void;
}

const ScenarioEvents: React.FC<Props> = ({ value, scenarioEvents, onChange }) => {
	const theme = useMantineTheme();
	const [visible, setVisible] = useState(value && value.length > 0 ? true : false);
	const [hours, setHours] = useState(24); // default to 0–24
	const [events, setEvents] = useState<ScenarioEvent[]>(value || []);
	const [modalOpened, setModalOpened] = useState(false);

	const [newEvent, setNewEvent] = useState<ScenarioEvent>({
		time: 0,
		duration: 1,
		event_id: parseInt(Object.keys(scenarioEvents)[0]) || 0,
	});

	const addHourRange = () => setHours((h) => h + 12);

	const handleAddEvent = () => {
		setEvents((prev) => [...prev, newEvent]);
		setModalOpened(false);
		setNewEvent({
			time: 0,
			duration: 1,
			event_id: parseInt(Object.keys(scenarioEvents)[0]) || 0,
		});
	};

	useEffect(() => {
		if (events && visible) {
			onChange(events);
		} else if (!visible) {
			onChange(null);
		}
	}, [events, visible]);

	return (
		<Flex
			direction="column"
			gap="md"
			style={{
				justifyContent: 'center',
				alignItems: 'flex-start',
				flexGrow: 1,
				position: 'relative',
				maxWidth: '100%',
			}}
		>
			<Group position="left">
				<Switch checked={visible} onChange={() => setVisible((v) => !v)} />
				<Group position="left">
					<Button onClick={addHourRange} disabled={!visible}>
						+12h
					</Button>
					<Button onClick={() => setModalOpened(true)} disabled={!visible}>
						Add Event
					</Button>
					<Button
						onClick={() => {
							// Randomize events by assigning random event IDs for already existing events
							const ids = Object.keys(scenarioEvents);
							const randomizedEvents = events.map((e) => ({
								...e,
								event_id:
									parseInt(ids[Math.floor(Math.random() * ids.length)]) || 0,
							}));
							setEvents(randomizedEvents);
						}}
						disabled={!visible}
					>
						Randomize
					</Button>
				</Group>
			</Group>

			<Box w="100%" maw="100%" style={{ alignSelf: 'stretch', position: 'relative' }}>
				<Collapse in={visible}>
					<Box
						w="100%"
						h={180}
						style={{
							border: '1px solid #ccc',
							borderRadius: 8,
							overflow: 'hidden',
						}}
					>
						<ScrollArea
							type="auto"
							w={'100%'}
							style={{ height: '100%', width: '100%' }}
						>
							<Box
								pos="relative"
								w={hours * 100}
								h={160}
								style={{
									backgroundColor:
										theme.colorScheme === 'dark'
											? theme.colors.dark[8]
											: theme.colors.light[2],
									maxWidth: '100%',
									overflow: 'auto',
								}}
							>
								{/* Hour Grid */}
								{Array.from({ length: hours }, (_, hour) => (
									<Box
										key={hour}
										pos="absolute"
										left={hour * 100}
										top={0}
										w={100}
										h="100%"
										style={{
											borderRight: '1px solid #dee2e6',
											boxSizing: 'border-box',
											padding: 4,
										}}
									>
										<Text size="xs" c="dimmed">
											{hour}:00
										</Text>
									</Box>
								))}

								{/* Events components (absolute positioning based on time/duration) */}
								{events.map((e, idx) => {
									const eventData = scenarioEvents[e.event_id];
									return (
										<Event
											key={idx}
											event={e}
											eventData={eventData}
											setEvents={setEvents}
										/>
									);
								})}
							</Box>
						</ScrollArea>
					</Box>
				</Collapse>
			</Box>

			{/* Modal to define a new event */}
			<Modal
				opened={modalOpened}
				onClose={() => setModalOpened(false)}
				title="Add Scenario Event"
				sx={{
					'& .mantine-Modal-body': {
						backgroundColor:
							theme.colorScheme === 'dark'
								? theme.colors.dark[7]
								: theme.colors.light[1],
						paddingTop: `${theme.spacing.xl} !important`,
						gap: theme.spacing.xs,
						display: 'flex',
						flexDirection: 'column',
					},
				}}
			>
				<Select
					label="Event"
					data={Object.values(scenarioEvents).map((e) => ({
						label: e.name,
						value: e.id.toString(),
					}))}
					value={newEvent.event_id.toString()}
					onChange={(val) =>
						setNewEvent((prev) => ({
							...prev,
							event_id: parseInt(val || '0', 10),
						}))
					}
				/>

				{/* Show event parameters */}
				<EventParameters eventInformation={scenarioEvents[newEvent.event_id] || {}} />

				<NumberInput
					label="Start time (e.g. 2.5 = 2:30)"
					value={newEvent.time}
					min={0}
					max={hours - 1}
					step={0.1}
					precision={1}
					labelProps={{
						style: {
							color:
								theme.colorScheme === 'dark'
									? theme.colors.dark[2]
									: theme.colors.light[2],
						},
					}}
					onChange={(val) =>
						setNewEvent((prev) => ({
							...prev,
							time: typeof val === 'number' ? val : 0,
						}))
					}
				/>
				<NumberInput
					label="Duration (hours)"
					value={newEvent.duration}
					min={0.5}
					step={0.5}
					precision={1}
					labelProps={{
						style: {
							color:
								theme.colorScheme === 'dark'
									? theme.colors.dark[2]
									: theme.colors.light[2],
						},
					}}
					onChange={(val) =>
						setNewEvent((prev) => ({
							...prev,
							duration: typeof val === 'number' ? val : 1,
						}))
					}
				/>
				<Group mt="md" position="right">
					<Button onClick={handleAddEvent}>Add</Button>
				</Group>
			</Modal>
		</Flex>
	);
};

export default ScenarioEvents;
